import {
  ALERT_ERROR,
  BOOT,
  FULFILLED,
  LOGIN,
  LOGOUT,
  PASSWORD_FORGOT,
  PASSWORD_RECOVERY,
  PASSWORD_UPDATE,
  REJECTED,
  UPDATE_ADMIN_ACCOUNT
} from 'actionTypes';
import history from 'appHistory';
import { fork, put, select, takeLatest } from 'redux-saga/effects';
import { apiRequest } from 'utils/request';
import { whoAmI } from 'utils/startApp';

// actions
export const login = (username, password) => ({
  type: LOGIN,
  payload: { username, password }
});

export const logout = () => ({
  type: LOGOUT
});

export const passwordUpdate = newPassword => ({
  type: PASSWORD_UPDATE,
  payload: { newPassword }
});

export const passwordRecovery = password => ({
  type: PASSWORD_RECOVERY,
  payload: password
});

export const passwordForgot = email => ({
  type: PASSWORD_FORGOT,
  payload: email
});

export const updateAdminAccount = id => ({
  type: UPDATE_ADMIN_ACCOUNT,
  payload: id
});


// reducer
// The CONTEXT: who is asking, and from where. Two ids, both from one answer -
// `/users/me/` on a page load, `/login/` on a sign-in.
//
// `state.users` also holds an `account_id`, on the caller's row, and it is the
// same number. It is not the same fact. That one is a column of a user; this is
// where the session is standing - which is why it belongs to authentication and
// not to the collection, and why reading it here is not keeping a second copy of
// a row.
//
// Spelled `account_id` and not `accountId`, which is the API's convention rather
// than this language's. Deliberate, and only here: this slice holds the answer to
// `/users/me/` as it came, so nothing has to be translated on the way to the
// query string that asks with it.
//
// The row itself is still not here, and that part has not changed: it lives in
// `state.users` with every other row and is read through
// `selectors/currentUser`. A copy of the ROW is what made the header name the
// previous customer after a switch.
//
// The context is what makes the fetching possible at all: every scoped
// collection names the account in its URL, and `state.users` is filled BY those
// requests - so the account cannot come from there. It cannot go stale within a
// page either: switching account empties the store and asks `/users/me/` again.
const INITIAL_STATE = {
  id: undefined,
  account_id: undefined,
  isAuthenticated: false,
  passwordRecoverSubmitted: false,
  passwordForgotSubmitted: false,
};

export default (state = INITIAL_STATE, { type, payload }) => {
  switch (type) {
    case LOGIN + FULFILLED:
    case UPDATE_ADMIN_ACCOUNT + FULFILLED:
      return {
        ...state,
        id: payload.id,
        account_id: payload.account_id,
        isAuthenticated: true
      };
    case PASSWORD_UPDATE + FULFILLED:
      return state;
    case PASSWORD_RECOVERY + FULFILLED:
      return {
        ...state,
        passwordRecoverSubmitted: true
      };
    case PASSWORD_FORGOT + FULFILLED:
      return {
        ...state,
        passwordForgotSubmitted: true
      };
    default:
      return state;
  }
};

// sagas
function* loginSaga({ payload }) {
  try {
    // One request. It sets the cookie and answers `{ id, account_id }` - the
    // same shape as /users/me, so this path and a page reload converge on the
    // same three lines: I know who, I know where, start the boot.
    const response = yield apiRequest('/login/', 'POST', {
      username: payload.username,
      password: payload.password
    });
    // `{ id, account_id }` straight into the store, and the boot needs no
    // argument: every scoped collection reads the account from there when its
    // fetch runs.
    yield put({ type: LOGIN + FULFILLED, payload: response.data });
    yield put({ type: BOOT });

    history.push('/');

  } catch (error) {
    // `error.response` exists only when the server answered. A request that
    // never got an answer at all - connection refused, CORS, DNS, a timeout -
    // arrives here with no response, and reading `.data` off it raised a second
    // TypeError that replaced the first one. The original never reached the
    // console, and every such failure was reported as a login error.
    const detail = error.response && error.response.data && error.response.data.detail;
    console.error('Login failed:', error.response || error);
    yield put({ type: LOGIN + REJECTED, payload: error });
    yield put({ type: ALERT_ERROR, payload: detail || 'Unrecognized Login Error, contact ReachOK Admin' });
  }
}

function* logoutSaga() {
  try {
    // The server owns the cookie, so ending the session is its call to make.
    // The token in the database is left alone: with one token per user, rolling
    // it would log the person out of every device, which is revocation and not
    // this.
    yield apiRequest('/logout/', 'POST');
  } catch (error) {
    // A logout that cannot reach the server still has to reach the login page.
    console.error('Logout failed:', error.response || error);
  } finally {
    history.push('/login');
  }
}

function* passwordUpdateSaga({ payload: { newPassword: password } }) {
  try {
    // The route hangs off the user, and the user is whoever is signed in - so
    // the id comes out of the store rather than off the wire. There is no old
    // password: this application has no recovery flow at all, so requiring one
    // would turn forgetting into a wall. USERS.md says what that costs.
    const id = yield select(state => state.auth.id);
    yield apiRequest(`/users/${id}/update_password`, 'PUT', { password });
    yield put({ type: PASSWORD_UPDATE + FULFILLED });
    // Changing the password does not roll the token, so the session survives.
    // Signing out anyway is the safer habit and is what this always did.
    yield put({ type: LOGOUT });
  } catch (error) {
    console.error('Password update failed:', error.response || error);
    yield put({ type: PASSWORD_UPDATE + REJECTED, payload: error });
  }
}

// TODO test recovery flow and view passwordRecover Submited and passwordForgotSubmitted

function* passwordRecoverySaga({ payload }) {
  try {
    const response = yield apiRequest('/PASSWORD RECOVERY URL/', 'POST', { password: payload });
    yield put({ type: PASSWORD_RECOVERY + FULFILLED });
    history.push('/login');
  } catch (error) {
    console.log('error: ', error);
    yield put({ type: PASSWORD_RECOVERY + REJECTED, payload: error });
  }
}

function* passwordForgotSaga({ payload }) {
  try {
    const response = yield apiRequest('/PASSWORD FORGOT URL/', 'POST', { email: payload });
    yield put({ type: PASSWORD_FORGOT + FULFILLED });
    history.push('/login');
  } catch (error) {
    console.log('error: ', error);
    yield put({ type: PASSWORD_FORGOT + REJECTED, payload: error });
  }
}

function* updateAdminAccountSaga({ payload }) {
  try {
    // 204, and nothing to read.
    const id = yield select(state => state.auth.id);
    yield apiRequest(`/users/${id}/change_account`, 'PUT', { account_id: payload });

    // Asked, not assumed from `payload`: if the switch did not take, this says so.
    const quem = yield whoAmI();

    // ONE dispatch: `reducers.js` empties every collection on it and the case
    // above writes the new context, so no render sees one customer's rows beside
    // another's account.
    yield put({ type: UPDATE_ADMIN_ACCOUNT + FULFILLED, payload: quem });
    yield put({ type: BOOT });

    // No navigation. The URL is a list with no id in it, so it is as valid for
    // the next customer; `components/PrivateRoute` remounts it.
  } catch (error) {
    console.error('Account switch failed:', error.response || error);
    yield put({ type: UPDATE_ADMIN_ACCOUNT + REJECTED, payload: error });
  }
}

function* watchLogin() {
  yield takeLatest(LOGIN, loginSaga);
}

function* watchLogout() {
  yield takeLatest(LOGOUT, logoutSaga);
}

function* watchPasswordUpdate() {
  yield takeLatest(PASSWORD_UPDATE, passwordUpdateSaga);
}

function* watchPasswordRecovery() {
  yield takeLatest(PASSWORD_RECOVERY, passwordRecoverySaga);
}

function* watchPasswordForgot() {
  yield takeLatest(PASSWORD_FORGOT, passwordForgotSaga);
}

function* watchUpdateAdminAccountSaga() {
  yield takeLatest(UPDATE_ADMIN_ACCOUNT, updateAdminAccountSaga);
}

const sagas = [
  fork(watchLogin),
  fork(watchLogout),
  fork(watchPasswordUpdate),
  fork(watchPasswordRecovery),
  fork(watchPasswordForgot),
  fork(watchUpdateAdminAccountSaga)
];

export { sagas };
